import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2, _templateObject3;
import JwtDecode from 'jwt-decode';
import { OktaAuth } from '@okta/okta-auth-js';
import { MD5 } from 'crypto-js';
import { EMPTY_OBJECT, EMPTY_STRING } from '@tekion/tekion-base/app.constants';
import CookieHelper from './CookieHelper';
import { isEmpty, sanitizeUrl, getValueFromEnv } from './commonHelper';
function tapEncrypt(_, key) {
  return "tap-".concat(MD5(key));
}

// function tapDecrypt(_, key) {
//   const accessKey = key.replace(/tap-/, '');
//   AES.decrypt(accessKey, TAP_SECRET).toString(enc.Utf8);
// }

const tokenProvider = {
  getItem(key) {
    const tokenKey = tapEncrypt(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), key);
    return localStorage.getItem(tokenKey);
  },
  setItem(key, val) {
    const tokenKey = tapEncrypt(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["", ""])), key);
    localStorage.setItem(tokenKey, val);
  },
  removeItem(key, hashKey) {
    const tokenKey = hashKey ? tapEncrypt(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["", ""])), key) : key;
    localStorage.removeItem(tokenKey);
  }
};
class TokenManager {
  constructor() {
    this.oktaAuthClient = null;
    this.tokenProvider = tokenProvider;
    this.userDetails = {
      email: '',
      uname: ''
    };
    this.accessTokenKey = 'access_token';
    this.tokenExpiryKey = 'tap-token-expiry';
    this.tokenExpiryDate = 'access_token_expire_date';
    this.providerTokenKey = 'okta-token-storage';
    this.provideCacheKey = 'okta-cache-storage';
    this.applyUserDetails();
  }
  applyUserDetails() {
    if (this.isAuthenticated()) {
      this.userDetails = {
        uname: this.getItem('uname'),
        email: this.getItem('email')
      };
    }
  }
  getAuthClient() {
    let appCallbackUri = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '/app/callback';
    if (this.oktaAuthClient === null) {
      const baseUrl = getValueFromEnv('OKTA_BASE_URL');
      const clientId = getValueFromEnv('OKTA_CLIENT_ID');
      const redirectUri = "".concat(window.location.origin).concat(appCallbackUri);
      this.oktaAuthClient = new OktaAuth({
        issuer: baseUrl,
        clientId: clientId,
        redirectUri,
        token: {
          storageProvider: this.tokenProvider
        }
      });
    }
    return this.oktaAuthClient;
  }
  setAuthClient() {
    let authClient = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    this.oktaAuthClient = authClient;
  }
  isAuthenticated() {
    return CookieHelper.getCookie('isAuthenticated');
  }
  getItem(key) {
    if (key === this.accessTokenKey) {
      if (CookieHelper.getCookie('isAuthenticated') === null) {
        this.clearTokenDetails();
        this.clearAllCookies();
      }
    }
    return this.tokenProvider.getItem(key);
  }
  setItem(key) {
    let value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    this.tokenProvider.setItem(key, value);
  }
  removeItem(key) {
    let hashKey = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    this.tokenProvider.removeItem(key, hashKey);
  }
  clearTokenDetails() {
    const keysToClear = [this.accessTokenKey, this.tokenExpiryDate, this.providerTokenKey, this.provideCacheKey, 'uname', 'idToken'];
    keysToClear.forEach(key => this.removeItem(key));
  }
  clearAllCookies() {
    CookieHelper.clearAllCookies();
  }

  /**
   * @description Function to parse access token and authenticate user in TAP
   * @param {String} accessToken - OAuth access token
  */
  persistUserAuth(authState) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    try {
      const {
        authenticateApiUrl
      } = options;
      const {
        accessToken: {
          accessToken
        },
        idToken: {
          idToken = ''
        } = {}
      } = authState;
      const userObject = JwtDecode(accessToken);
      const {
        exp,
        sub: email,
        uid
      } = userObject;
      const userEmail = email && email.toLowerCase();
      const time = parseInt("".concat(exp, "000"), 10);
      let name = '';
      if (idToken !== '') {
        const uObject = JwtDecode(idToken);
        name = uObject.name;
      }
      this.userDetails = {
        email: userEmail,
        fullName: name,
        tokenExpiry: time
      };
      this.setItem('email', userEmail);
      this.setItem(this.tokenExpiryKey, time);
      this.setItem(this.tokenExpiryDate, new Date(time).toUTCString());
      this.setItem(this.accessTokenKey, accessToken);
      this.setItem('idToken', idToken);
      this.removeItem('okta-token-storage', false);
      CookieHelper.setCookie('isAuthenticated', true, {
        time
      });
      CookieHelper.eraseCookie('okta-token-storage_idToken');
      const url = decodeURIComponent(CookieHelper.getCookie('tap_login_redirect_uri'));
      const isPartnersSite = getValueFromEnv('PARTNERS_SITE', 'false');
      const landingPageUrl = getValueFromEnv('LANDING_PAGE_URL', '');
      const defaultUrl = landingPageUrl || (isPartnersSite === 'true' ? '/home/mytasks' : '/app/home');
      const redirectUri = url && url !== 'null' ? url : defaultUrl;
      this.doPost(authenticateApiUrl, {
        email: userEmail,
        oktaid: uid
      }).then(() => {
        CookieHelper.eraseCookie('tap_login_redirect_uri');
        window.location.href = redirectUri;
      }).catch(() => {
        console.error("API Url Authentication failed...");
        CookieHelper.setCookie('tap_auth_failed', "true");
        CookieHelper.eraseCookie('tap_login_redirect_uri');
        window.location.href = redirectUri;
      });
    } catch (e) {
      console.error(e);
      this.clearTokenDetails();
      this.clearAllCookies();
    }
  }
  getUserDetails() {
    return this.userDetails;
  }
  doPost(url, data) {
    let customHeader = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    let tokenInfo = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : EMPTY_OBJECT;
    const contentType = 'application/json; charset=UTF-8';
    const header = customHeader || {};
    let dataObj = data;
    if (!customHeader) {
      header['Content-Type'] = contentType;
      dataObj = JSON.stringify(data);
    }
    if (isEmpty(tokenInfo)) {
      header['tap-api-token'] = this.getItem(this.accessTokenKey);
    } else {
      const {
        tokenPropertyName = EMPTY_STRING,
        token = EMPTY_STRING
      } = tokenInfo;
      header[tokenPropertyName] = token;
    }
    return fetch(url, Object.assign({}, {
      method: 'POST',
      headers: header,
      body: dataObj
    })).then(res => this.processResponse(res));
  }
  processResponse(res) {
    let response = null;
    if (res.ok) {
      response = res.json();
    } else {
      response = Promise.reject(res);
    }
    return response;
  }
}
export default new TokenManager();